import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button, Typography } from '@mui/material';

import { notificationService } from '@arrived/common';
import { useResendMFAEmail } from '@arrived/hooks';
import { User } from '@arrived/models';
import { useGetMFADetailsQuery, usePostResetMFAMutation } from '@arrived/queries';

export const useProfileMFAContent = (user?: User) => {
  const { mutateAsync: resetMFA, isLoading: resetIsLoading } = usePostResetMFAMutation({
    onSuccess: () =>
      notificationService.success(
        <FormattedMessage
          id="profile.security.items.mfa.disableSuccess"
          defaultMessage="MFA has been successfully disabled"
        />
      ),
    onError: () =>
      notificationService.error(
        <FormattedMessage
          id="profile.security.items.mfa.disableError"
          defaultMessage="There was an issue with disabling MFA"
        />
      ),
  });

  const handleResetMFA = useCallback(() => user && resetMFA(user.id), [user, resetMFA]);

  const { handleResendMFAEmail, enrollIsLoading, isBlocked, counter } = useResendMFAEmail(user?.id);
  const mfaDetailsState = useGetMFADetailsQuery(user?.id, { refetchOnWindowFocus: isBlocked });
  const isEnrolled = useMemo(() => mfaDetailsState.data?.enrollmentStatus === 'ENROLLED', [mfaDetailsState]);

  const enrollContent = useMemo(() => {
    if (mfaDetailsState.isLoading) return null;

    let content = (
      <Button variant="outlined" size="small" fullWidth onClick={handleResendMFAEmail} disabled={enrollIsLoading}>
        <FormattedMessage id="profile.security.items.mfa.enroll" defaultMessage="Set Up Verification" />
      </Button>
    );

    if (isBlocked) {
      content = (
        <Typography>
          <FormattedMessage
            id="profile.security.items.mfa.enrollCounter"
            defaultMessage="Please wait {counter} seconds before sending another enrollment email"
            values={{ counter }}
          />
        </Typography>
      );
    }

    return content;
  }, [user, mfaDetailsState, enrollIsLoading, handleResendMFAEmail, counter, isBlocked]);

  return useMemo(() => {
    if (!user) return null;
    return isEnrolled ? (
      <Button variant="outlined" size="small" fullWidth onClick={handleResetMFA} disabled={resetIsLoading}>
        <FormattedMessage id="profile.security.items.mfa.disable" />
      </Button>
    ) : (
      enrollContent
    );
  }, [user, isEnrolled, handleResetMFA, resetIsLoading, enrollContent]);
};
